import { BASE_URL } from "./schema/constants";
import { PATHS } from "@/shared/publicPaths";

export type PageMeta = {
  title: string;
  description: string;
  noindex?: boolean;
};

/** Default public meta when a path has no specific entry. */
export const DEFAULT_PAGE_META: PageMeta = {
  title: "Jandi | Servicios del Hogar en Puerto Rico",
  description:
    "Plomeros, electricistas, limpieza, cuidadores y más profesionales verificados cerca de ti. Pide tu cotización gratis y contrata con confianza en Jandi.",
};

const PAGE_METADATA: Record<string, PageMeta> = {
  "/": DEFAULT_PAGE_META,
  [PATHS.services]: {
    title: "Profesionales Confiables para tu Hogar en Puerto Rico | Jandi",
    description:
      "Explora los servicios disponibles en Jandi: plomería, electricidad, limpieza, jardinería, handyman, cuidado de infantes y adultos mayores en toda la isla.",
  },
  [PATHS.faq]: {
    title: "Cómo Funciona Jandi | Preguntas Frecuentes en PR",
    description:
      "¿Cómo pedir una cotización? ¿Es seguro contratar en Jandi? Resuelve todas tus dudas sobre pagos, profesionales verificados y más en Puerto Rico.",
  },
  [PATHS.about]: {
    title: "Lo Que Nos Mueve: Nuestra Misión | Jandi",
    description:
      "Jandi conecta a las familias de Puerto Rico con profesionales locales confiables, impulsando el talento y la economía de nuestra isla.",
  },
  [PATHS.login]: {
    title: "Ingresar a tu Cuenta | Jandi",
    description:
      "Accede a tu cuenta de Jandi para gestionar tus servicios, cotizaciones y tu perfil profesional.",
    noindex: true,
  },
  [PATHS.signup]: {
    title: "Crear Cuenta Gratis | Jandi Puerto Rico",
    description:
      "Regístrate gratis en Jandi. Encuentra profesionales para tu hogar o consigue clientes ofreciendo tus servicios en todo Puerto Rico.",
    noindex: true,
  },
  [PATHS.signupWorker]: {
    title: "Crear Cuenta Gratis | Jandi Puerto Rico",
    description:
      "Regístrate gratis en Jandi. Encuentra profesionales para tu hogar o consigue clientes ofreciendo tus servicios en todo Puerto Rico.",
  },
  [PATHS.signupCustomer]: {
    title: "Crear Cuenta Gratis | Jandi Puerto Rico",
    description:
      "Regístrate gratis en Jandi. Encuentra profesionales para tu hogar o consigue clientes ofreciendo tus servicios en todo Puerto Rico.",
  },
  [PATHS.privacy]: {
    title: "Política de Privacidad | Jandi",
    description:
      "Lee cómo Jandi protege tu información personal y tus datos cuando usas nuestra plataforma de servicios en Puerto Rico.",
  },
  [PATHS.terms]: {
    title: "Términos de Uso | Jandi",
    description:
      "Consulta los términos y condiciones de uso de la plataforma Jandi para clientes y profesionales en Puerto Rico.",
  },
  [PATHS.textMessage]: {
    title: "Política de Mensajes de Texto | Jandi",
    description:
      "Conoce nuestra política de mensajes de texto: consentimiento, frecuencia de envíos y cómo darte de baja de las notificaciones SMS de Jandi.",
  },
};

export function normalizePathname(pathname: string): string {
  const path = (pathname || "/").split("?")[0].split("#")[0];
  if (!path || path === "/") return "/";
  return path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
}

export function getPageMeta(pathname: string): PageMeta {
  const path = normalizePathname(pathname);
  if (PAGE_METADATA[path]) return PAGE_METADATA[path];

  return DEFAULT_PAGE_META;
}

/** Robots directive for a path (`noindex, follow` only on /ingresar and /registro). */
export function getRobotsContent(pathname: string): string {
  const path = normalizePathname(pathname);
  const meta = PAGE_METADATA[path] ?? getPageMeta(path);
  if (meta.noindex) return "noindex, follow";
  return "index, follow";
}

export function getCanonicalUrl(pathname: string): string {
  const path = normalizePathname(pathname);
  return path === "/" ? `${BASE_URL}/` : `${BASE_URL}${path}`;
}

/** Vike `title` resolver for known static paths (SSR + SPA shell). */
export function titleFromPageContext(pageContext: {
  urlPathname?: string;
}): string {
  return getPageMeta(pageContext.urlPathname || "/").title;
}

export function homePageTitle(): string {
  return PAGE_METADATA["/"].title;
}

export function servicesPageTitle(): string {
  return PAGE_METADATA[PATHS.services].title;
}
